import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Space, message } from 'antd';
import { PlusOutlined, PlayCircleOutlined, EyeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db, COLLECTIONS } from '../config/firebase';
import type { ColumnsType } from 'antd/es/table';

interface LiveTest {
  id: string;
  title: string;
  subject: string;
  duration_minutes: number;
  total_questions: number;
  status: 'scheduled' | 'live' | 'completed' | 'cancelled';
  access_type: 'organization' | 'public';
  scheduled_at: any;
  created_by_name: string;
  organization_name?: string;
}

export default function LiveTestList() {
  const navigate = useNavigate();
  const [liveTests, setLiveTests] = useState<LiveTest[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Subscribe to live tests
    const q = query(collection(db, COLLECTIONS.LIVE_TESTS));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const tests: LiveTest[] = [];
      snapshot.forEach((doc) => {
        tests.push({ id: doc.id, ...doc.data() } as LiveTest);
      });
      
      // Sort by scheduled date
      tests.sort((a, b) => {
        const dateA = a.scheduled_at?.toDate ? a.scheduled_at.toDate() : new Date(a.scheduled_at);
        const dateB = b.scheduled_at?.toDate ? b.scheduled_at.toDate() : new Date(b.scheduled_at);
        return dateB.getTime() - dateA.getTime();
      });
      
      setLiveTests(tests);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const getStatusTag = (status: string) => {
    const statusConfig: Record<string, { color: string; text: string }> = {
      scheduled: { color: 'blue', text: 'Scheduled' },
      live: { color: 'red', text: 'LIVE' },
      completed: { color: 'green', text: 'Completed' },
      cancelled: { color: 'default', text: 'Cancelled' },
    };

    const config = statusConfig[status] || statusConfig.scheduled;
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const formatDateTime = (date: any) => {
    const d = date?.toDate ? date.toDate() : new Date(date);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const columns: ColumnsType<LiveTest> = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      width: '25%',
    },
    {
      title: 'Subject',
      dataIndex: 'subject',
      key: 'subject',
      width: '10%',
    },
    {
      title: 'Questions',
      dataIndex: 'total_questions',
      key: 'total_questions',
      width: '8%',
      align: 'center',
    },
    {
      title: 'Duration',
      dataIndex: 'duration_minutes',
      key: 'duration_minutes',
      width: '8%',
      render: (minutes: number) => `${minutes} min`,
    },
    {
      title: 'Access',
      dataIndex: 'access_type',
      key: 'access_type',
      width: '10%',
      render: (type: string, record: LiveTest) => (
        <div>
          <Tag color={type === 'public' ? 'green' : 'orange'}>
            {type === 'public' ? 'Public' : 'Organization'}
          </Tag>
          {record.organization_name && (
            <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>
              {record.organization_name}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: '10%',
      render: (status: string) => getStatusTag(status),
    },
    {
      title: 'Scheduled',
      dataIndex: 'scheduled_at',
      key: 'scheduled_at',
      width: '15%',
      render: (date: any) => formatDateTime(date),
    },
    {
      title: 'Created By',
      dataIndex: 'created_by_name',
      key: 'created_by_name',
      width: '12%',
    },
    {
      title: 'Action',
      key: 'action',
      width: '10%',
      render: (_: any, record: LiveTest) => (
        <Space>
          {record.status === 'scheduled' || record.status === 'live' ? (
            <Button
              type="primary"
              size="small"
              icon={<PlayCircleOutlined />}
              onClick={() => navigate(`/live-tests/control/${record.id}`)}
            >
              {record.status === 'live' ? 'Control' : 'Start'}
            </Button>
          ) : (
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/live-tests/results/${record.id}`)}
            >
              View
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title="Live Tests"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/live-tests/create')}
          >
            Create Live Test
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={liveTests}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} tests`,
          }}
        />
      </Card>
    </div>
  );
}
